/**
 * Enum for all supported task types in the application
 * 
 * These task types correspond to the tasks that can be enqueued
 * using the task-enqueue edge function.
 */
export enum TaskType {
  SCREENSHOT_PAGE = 'SCREENSHOT_PAGE',
  EXTRACT_PRICES = 'EXTRACT_PRICES',
  VERIFY_IMAGE = 'VERIFY_IMAGE',
  EXTRACT_METRICS = 'EXTRACT_METRICS',
  EXTRACT_TAGS = 'EXTRACT_TAGS',
  PROCESS_METRIC_EVENTS = 'PROCESS_METRIC_EVENTS'
}

/**
 * Type guard to check if a string is a valid TaskType
 * 
 * @param value - The string to check
 * @returns True if the string is a valid TaskType
 */
export function isValidTaskType(value: string): value is TaskType {
  return Object.values(TaskType).includes(value as TaskType);
}

/**
 * Get a human-readable description for a task type
 * 
 * @param taskType - The task type to get a description for
 * @returns A human-readable description
 */
export function getTaskTypeDescription(taskType: TaskType): string {
  switch (taskType) {
    case TaskType.SCREENSHOT_PAGE:
      return 'Take a screenshot of the pricing page';
    case TaskType.EXTRACT_PRICES:
      return 'Extract pricing information';
    case TaskType.VERIFY_IMAGE:
      return 'Verify screenshot image';
    case TaskType.EXTRACT_METRICS:
      return 'Extract pricing metrics';
    case TaskType.EXTRACT_TAGS:
      return 'Extract tags from pricing page';
    case TaskType.PROCESS_METRIC_EVENTS:
      return 'Process metric events';
    default:
      return 'Unknown task type';
  }
}

/**
 * Get an icon name for a task type (for use with Lucide icons)
 * 
 * @param taskType - The task type to get an icon for
 * @returns The name of a Lucide icon
 */
export function getTaskTypeIcon(taskType: TaskType): string {
  switch (taskType) {
    case TaskType.SCREENSHOT_PAGE:
      return 'Camera';
    case TaskType.EXTRACT_PRICES:
      return 'DollarSign';
    case TaskType.VERIFY_IMAGE:
      return 'CheckCircle';
    case TaskType.EXTRACT_METRICS:
      return 'BarChart';
    case TaskType.EXTRACT_TAGS:
      return 'Tag';
    case TaskType.PROCESS_METRIC_EVENTS:
      return 'Activity';
    default:
      return 'Circle';
  }
}
